import jwtDecode, { JwtPayload } from 'jwt-decode';
import { BehaviorSubject } from 'rxjs';
import { User } from '../../models/Auth';
import api from '../../api/apiService';

const moduleUri = '/authentication/v1/auth';
export const LOGIN_URI = `${moduleUri}/login`;
export const LOGOUT_URI = `${moduleUri}/logout`;
export const REFRESH_TOKEN_URI = `${moduleUri}/token`;

const ACCESS_TOKEN_NAME = 'csci-access-token';
const REFRESH_TOKEN_NAME = 'csci-refresh-token';

export const getAccessToken = (): string | null => localStorage.getItem(ACCESS_TOKEN_NAME);
const getRefreshToken = (): string | null => localStorage.getItem(REFRESH_TOKEN_NAME);

export const auth$ = new BehaviorSubject(getAccessToken());
let refreshTokenIntervalId;

export const signIn = async (username: string, password: string): Promise<void> => {
  const { data } = await api.post(LOGIN_URI, {
    username,
    password,
    applicationId: 'fci',
  });
  const { accessToken, refreshToken } = data;

  setTokens(accessToken, refreshToken);
  auth$.next(accessToken);
};

const refreshAccessToken = async () => {
  try {
    const { data } = await api.get(REFRESH_TOKEN_URI, {
      headers: {
        authorization: `Bearer ${getRefreshToken()}`,
      },
    });
    const { accessToken, refreshToken } = data;

    setTokens(accessToken, refreshToken);
    return decode(accessToken);
  } catch (e) {
    return null;
  }
};

export const onTokenChange = async (token: string | null): Promise<void> => {
  if (token) {
    const user = await refreshAccessToken();
    if (user) {
      refreshTokenIntervalId = setInterval(refreshAccessToken, 298 * 1000);
    }
  } else {
    clearInterval(refreshTokenIntervalId);
  }
};

export const signOut = async (): Promise<void> => {
  try {
    const { data } = await api.get(LOGOUT_URI);
    const { fitchAuthLogoutUrl } = data;
    if (fitchAuthLogoutUrl) {
      setTimeout(() => {
        window.location.replace(fitchAuthLogoutUrl);
      }, 100);
    }
  } catch (e) {}
  clearTokens();
  auth$.next(null);
};

const setTokens = (accessToken, refreshToken) => {
  localStorage.setItem(ACCESS_TOKEN_NAME, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_NAME, refreshToken);
  }
};

const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_NAME);
  localStorage.removeItem(REFRESH_TOKEN_NAME);
};

const decode = (token): JwtPayload => jwtDecode(token);

export const getCurrentUser = (): User => {
  const token = auth$.value;

  if (token) {
    const user: JwtPayload = decode(token);
    return { userId: user.sub, exp: user.exp };
  }

  return {};
};
